import { ShaderProgram } from '../renderer/ShaderProgram';
import { InstancedMesh } from '../renderer/InstancedMesh';
import type { Camera } from '../renderer/Camera';
import type { Renderable } from '../renderer/RenderPipeline';
import type { StellarRenderConfig } from '../config/StellarRenderConfig';
import type { ClusterContent } from '../network/ContentLoader';
import { axialToWorld } from '../math/HexMath';
import { createIcoSphere } from '../renderer/GeometryFactory';

import vertSrc from '../shaders/moon.vert.glsl?raw';
import prefixSrc from '../shaders/glsl-prefix.glsl?raw';
import noiseSrc from '../shaders/noise3d.glsl?raw';
import fragSrc from '../shaders/moon.frag.glsl?raw';

const FLOATS_PER_INSTANCE = 12;

export class MoonRenderer implements Renderable {
  private _shader: ShaderProgram;
  private _mesh: InstancedMesh;
  private _config: StellarRenderConfig;
  private _hexRadius: number;
  private _moonCount = 0;
  private _startTime = performance.now();
  private _instanceBuffer: Float32Array;

  constructor(
    gl: WebGL2RenderingContext,
    config: StellarRenderConfig,
    hexRadius: number,
    maxInstances: number,
  ) {
    this._config = config;
    this._hexRadius = hexRadius;
    this._instanceBuffer = new Float32Array(maxInstances * FLOATS_PER_INSTANCE);

    const combinedFrag = prefixSrc + noiseSrc + '\n' + fragSrc;

    this._shader = new ShaderProgram(gl, vertSrc, combinedFrag);
    const sphere = createIcoSphere(3);
    const geoData = this._interleaveSphere(sphere.positions, sphere.normals);

    this._mesh = new InstancedMesh(
      gl, this._shader, geoData,
      [
        { name: 'a_position', size: 3, divisor: 0 },
        { name: 'a_normal', size: 3, divisor: 0 },
      ],
      [
        { name: 'a_parentPos', size: 3, divisor: 1 },
        { name: 'a_orbitRadius', size: 1, divisor: 1 },
        { name: 'a_orbitSpeed', size: 1, divisor: 1 },
        { name: 'a_orbitPhase', size: 1, divisor: 1 },
        { name: 'a_inclinationX', size: 1, divisor: 1 },
        { name: 'a_inclinationZ', size: 1, divisor: 1 },
        { name: 'a_scale', size: 1, divisor: 1 },
        { name: 'a_color', size: 3, divisor: 1 },
      ],
      sphere.indices,
    );
  }

  updateFromContent(contents: ClusterContent[]): void {
    let count = 0;
    const max = this._instanceBuffer.length / FLOATS_PER_INSTANCE;
    
    for (let c = 0; c < contents.length; c++) {
      const planets = contents[c].planets;
      for (let i = 0; i < planets.length; i++) {
        if (count >= max) break;
        const p = planets[i];
        const numMoons = p.moon_count ?? 0;
        if (numMoons === 0) continue;
        if (p.axial_q == null || p.axial_r == null) continue;

        const [wx, wz] = axialToWorld(p.axial_q, p.axial_r, this._hexRadius);
        const parentSize = (p.scale ?? 1.0) * this._config.planetBaseSize;
        const baseSeed = (p.seed ?? 0);

        for (let m = 0; m < numMoons; m++) {
          if (count >= max) break;
          const mSeed = ((baseSeed + m * 12345) % 65536) / 65536.0;
          
          // Größerer Abstand zum Planeten (1.6x bis 2.5x des Planetenradius), da wir jetzt mehr Platz zwischen Planeten haben
          const orbitRadius = parentSize * (1.6 + m * 0.5 + mSeed * 0.4);
          
          // Viel langsamere Orbitalgeschwindigkeit (damit er nicht synchron mit dem Planeten kreist)
          // Der Planet rotiert mit 0.02, der Mond kreist jetzt sehr majestätisch (0.002 bis 0.005)
          const orbitSpeed = (0.002 + mSeed * 0.003) * (m % 2 === 0 ? 1 : -1);
          const orbitPhase = mSeed * Math.PI * 2.0;
          
          const inclinationX = (mSeed - 0.5) * 0.4;
          const inclinationZ = ((baseSeed * 7 % 100) / 100.0 - 0.5) * 0.4;
          
          // Monde wieder etwas größer machen (12% bis 18% der Standard-Planetengröße)
          const scale = this._config.planetBaseSize * (0.12 + mSeed * 0.06);
          
          const colorIntensity = 0.5 + mSeed * 0.4;

          const idx = count * FLOATS_PER_INSTANCE;
          this._instanceBuffer[idx]     = wx;
          this._instanceBuffer[idx + 1] = parentSize;
          this._instanceBuffer[idx + 2] = wz;
          this._instanceBuffer[idx + 3] = orbitRadius;
          this._instanceBuffer[idx + 4] = orbitSpeed;
          this._instanceBuffer[idx + 5] = orbitPhase;
          this._instanceBuffer[idx + 6] = inclinationX;
          this._instanceBuffer[idx + 7] = inclinationZ;
          this._instanceBuffer[idx + 8] = scale;
          this._instanceBuffer[idx + 9] = colorIntensity;
          this._instanceBuffer[idx + 10] = colorIntensity;
          this._instanceBuffer[idx + 11] = colorIntensity;
          count++;
        }
      }
    }
    
    this._moonCount = count;
    this._mesh.updateInstances(
      this._instanceBuffer.subarray(0, count * FLOATS_PER_INSTANCE), count,
    );
  }

  get moonCount(): number { return this._moonCount; }

  render(gl: WebGL2RenderingContext, camera: Camera): void {
    if (this._moonCount === 0) return;

    gl.enable(gl.DEPTH_TEST);
    gl.depthMask(true);
    gl.enable(gl.CULL_FACE);

    this._shader.use();
    this._shader.setMat4('u_projection', camera.projection.data);
    this._shader.setMat4('u_view', camera.view.data);
    this._shader.setFloat('u_time', (performance.now() - this._startTime) / 1000.0);
    this._shader.setVec3('u_cameraPos', camera.position.x, camera.position.y, camera.position.z);

    this._mesh.draw();

    gl.disable(gl.CULL_FACE);
  }

  dispose(): void {
    this._shader.dispose();
    this._mesh.dispose();
  }

  private _interleaveSphere(pos: Float32Array, norm: Float32Array): Float32Array {
    const count = pos.length / 3;
    const out = new Float32Array(count * 6);
    for (let i = 0; i < count; i++) {
      const i3 = i * 3, i6 = i * 6;
      out[i6] = pos[i3]; out[i6 + 1] = pos[i3 + 1]; out[i6 + 2] = pos[i3 + 2];
      out[i6 + 3] = norm[i3]; out[i6 + 4] = norm[i3 + 1]; out[i6 + 5] = norm[i3 + 2];
    }
    return out;
  }
}
